import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateFollowUpReport,
  useFollowUpReportList,
  FOLLOW_UP_REPORT_STATUS_LABELS,
  type FollowUpReportStatus,
} from '@/hooks/useFollowUp';
import {
  FOLLOW_UP_PERMISSIONS,
  canSeeFollowUpReportsMenu,
  hasPermission,
} from '@/lib/followUpPermissions';

const STATUS_OPTIONS: FollowUpReportStatus[] = [
  'draft',
  'submitted',
  'changes_requested',
  'approved',
  'published',
  'archived',
];

export default function FollowUpReportsPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { rows, total, page, limit, loading, error, fetchReports } =
    useFollowUpReportList();
  const [filterStatus, setFilterStatus] = useState<FollowUpReportStatus | ''>('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [search, setSearch] = useState('');
  const [pageInput, setPageInput] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);

  const canRead = canSeeFollowUpReportsMenu(permissions);
  const canCreate = hasPermission(permissions, FOLLOW_UP_PERMISSIONS.CREATE);

  useEffect(() => {
    if (!canRead) return;
    void fetchReports({
      status: filterStatus || undefined,
      customerId: filterCustomer || undefined,
      planId: filterPlan || undefined,
      search: search || undefined,
      page: pageInput,
      limit: 20,
    });
  }, [
    filterStatus,
    filterCustomer,
    filterPlan,
    search,
    pageInput,
    canRead,
    fetchReports,
  ]);

  if (!canRead) return <AccessDenied resource="随访报告" />;

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">随访报告</h1>
          <p className="text-sm text-muted-foreground">
            阶段性随访总结报告；草稿 → 审核 → 发布给客户。
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => {
              setCreateKey((k) => k + 1);
              setCreateOpen(true);
            }}
          >
            新建报告
          </Button>
        )}
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <Label htmlFor="filter-status">状态</Label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  (e.target.value || '') as FollowUpReportStatus | '',
                )
              }
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            >
              <option value="">全部</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {FOLLOW_UP_REPORT_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-customer">客户 ID</Label>
            <Input
              id="filter-customer"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-plan">计划 ID</Label>
            <Input
              id="filter-plan"
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-search">标题搜索</Label>
            <Input
              id="filter-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {error && (
        <Card className="border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error.message}
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left">
                <th className="px-3 py-2">标题</th>
                <th className="px-3 py-2">客户</th>
                <th className="px-3 py-2">状态</th>
                <th className="px-3 py-2">客户可见</th>
                <th className="px-3 py-2">更新时间</th>
                <th className="px-3 py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    加载中…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">
                    暂无报告
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2 font-medium">{row.title}</td>
                  <td className="px-3 py-2">{row.customerId}</td>
                  <td className="px-3 py-2">
                    {FOLLOW_UP_REPORT_STATUS_LABELS[row.status]}
                  </td>
                  <td className="px-3 py-2">
                    {row.customerVisible ? '是' : '否'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(row.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/follow-up/reports/${row.id}`)}
                    >
                      查看
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between text-sm">
        <div className="text-muted-foreground">
          共 {total} 条 · 第 {page} / {totalPages} 页
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput <= 1}
            onClick={() => setPageInput((p) => Math.max(1, p - 1))}
          >
            上一页
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={pageInput >= totalPages}
            onClick={() => setPageInput((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      </div>

      {createOpen && (
        <Card className="space-y-3 border-blue-200 bg-blue-50 p-4">
          <h2 className="text-base font-semibold">新建随访报告</h2>
          <CreateReportForm
            key={createKey}
            onCreated={(id) => {
              setCreateOpen(false);
              navigate(`/follow-up/reports/${id}`);
            }}
            onCancel={() => setCreateOpen(false)}
          />
        </Card>
      )}
    </div>
  );
}

function CreateReportForm({
  onCreated,
  onCancel,
}: {
  onCreated: (id: string) => void;
  onCancel: () => void;
}) {
  const { createReport, loading, error } = useCreateFollowUpReport();
  const [customerId, setCustomerId] = useState('');
  const [planId, setPlanId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');

  const submit = async () => {
    if (!customerId.trim() || !title.trim() || !content.trim()) return;
    const r = await createReport({
      customerId: customerId.trim(),
      planId: planId.trim() || undefined,
      title: title.trim(),
      content,
      summary: summary.trim() || undefined,
    });
    if (r) onCreated(r.id);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="r-cust">客户 ID</Label>
          <Input
            id="r-cust"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="r-plan">计划 ID（可选）</Label>
          <Input
            id="r-plan"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-title">标题</Label>
        <Input
          id="r-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={255}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-summary">摘要</Label>
        <textarea
          id="r-summary"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={2}
          maxLength={2000}
          className="w-full rounded-md border bg-background p-2 text-sm"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="r-content">内容（JSON 或富文本，必填）</Label>
        <textarea
          id="r-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className="w-full rounded-md border bg-background p-2 text-sm font-mono"
        />
      </div>
      {error && <div className="text-sm text-red-700">{error.message}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          取消
        </Button>
        <Button onClick={submit} disabled={loading}>
          {loading ? '提交中…' : '创建'}
        </Button>
      </div>
    </div>
  );
}
